import React, { useState } from 'react';
import { CreateProductDialog } from '../CreateProductDialog/CreateProductDialog';
import { UpsertCatalogProductsDialog } from '../UpsertCatalogProductsDialog';
import { UpsertCreditTypeDialog } from '../UpsertCreditTypeDialog';
import { useDialog } from '../../../../../../hooks/use-dialog';
import { withSuspenseBoundary } from '../../../../../../components/utility/SuspenseBoundary';
import { PRODUCT_TYPE, ProductType } from '../../ProductsTable/products-columns/products-columns-type';
import { isNotNullOrUndefined, Optional, raiseUnreachable, validateNotNullOrUndefined } from '@vy/utils';
import { VyDialog } from '../../../../../../components/new-design/feedback/VyDialog';
import { ProductCreationStepperDialogContent } from './ProductCreationStepperDialogContent.tsx';

type ProductCreationStepperDialogProps = {
  openCreateProductDialog?: boolean;
  onClose?: () => void;
  onProductCreated?: (productName: string) => void;
  children?: React.ReactElement;
  autoOpen?: boolean;
};

const ProductCreationStepperDialog: React.FC<ProductCreationStepperDialogProps> = ({
  openCreateProductDialog,
  onClose,
  onProductCreated,
  children,
  autoOpen,
}) => {
  const [selectedProductType, setSelectedProductType] = useState<Optional<ProductType>>(null);
  const [showMeteredDialog, setShowMeteredDialog] = useState(false);
  const [showStandardDialog, setShowStandardDialog] = useState(false);
  const [showCreditDialog, setShowCreditDialog] = useState(false);

  const { isDialogOpen, handleOpen, handleClose } = useDialog(autoOpen);

  const isControlledMode = isNotNullOrUndefined(openCreateProductDialog);
  const actualOpen = isControlledMode ? openCreateProductDialog : isDialogOpen;
  const isMainDialogOpen = actualOpen && !showMeteredDialog && !showStandardDialog && !showCreditDialog;

  const resetAllStates = () => {
    setSelectedProductType(null);
    setShowMeteredDialog(false);
    setShowStandardDialog(false);
    setShowCreditDialog(false);
  };

  const handleDialogClose = () => {
    resetAllStates();

    if (isControlledMode) {
      onClose?.();
    } else {
      handleClose();
    }
  };

  const handleProductTypeSelection = (type: ProductType) => {
    setSelectedProductType(type);
  };

  const handleNext = () => {
    validateNotNullOrUndefined(selectedProductType, 'Selected product type is required');

    switch (selectedProductType) {
      case PRODUCT_TYPE.METERED:
        setShowMeteredDialog(true);
        break;
      case PRODUCT_TYPE.SERVICE:
        setShowStandardDialog(true);
        break;
      case PRODUCT_TYPE.CREDIT:
        setShowCreditDialog(true);
        break;
      default:
        raiseUnreachable(selectedProductType);
    }
  };

  const handleMeteredDialogClose = () => {
    handleDialogClose();
  };

  const handleStandardDialogClose = () => {
    handleDialogClose();
  };

  const handleProductCreated = (productName: string) => {
    if (onProductCreated) {
      onProductCreated(productName);
    }

    handleDialogClose();
  };

  const handleReturnToMain = () => {
    setShowMeteredDialog(false);
    setShowStandardDialog(false);
    setShowCreditDialog(false);
    setSelectedProductType(null);
  };

  return (
    <>
      {children &&
        React.cloneElement(children, {
          onClick: handleOpen,
        })}

      <VyDialog
        open={isMainDialogOpen}
        onClose={handleDialogClose}
        label="Create new product"
        labelDescription="Define a new metered or standard product"
        ctaText="Next"
        ctaDisabled={!selectedProductType}
        cancelButtonText="Cancel"
        onSubmit={handleNext}
        maxWidth="md"
        disableDividers
        cancelButtonProps={{
          sx: {
            color: 'rgba(13, 27, 39, 0.87)',
          },
        }}
        ctaButtonProps={{
          sx: {
            fontSize: '16px',
            width: '166px',
            fontWeight: 500,
            lineHeight: '24px',
            color: selectedProductType ? '#FFF' : '#D6DBE7',
            backgroundColor: selectedProductType ? '#0D0D48' : '#AFB6C4',
            '&:hover': {
              backgroundColor: selectedProductType ? '#0D0D48' : '#AFB6C4',
            },
          },
        }}
      >
        <ProductCreationStepperDialogContent
          selectedProductType={selectedProductType}
          handleProductTypeSelection={handleProductTypeSelection}
        />
      </VyDialog>

      {showMeteredDialog && (
        <CreateProductDialog
          openCreateProductDialog={showMeteredDialog}
          onClose={handleMeteredDialogClose}
          onProductCreated={handleProductCreated}
          navigateBack={handleReturnToMain}
        />
      )}

      {showStandardDialog && (
        <UpsertCatalogProductsDialog
          openCreateProductDialog={showStandardDialog}
          onClose={handleStandardDialogClose}
          onProductCreated={handleProductCreated}
          onBackToMain={handleReturnToMain}
        />
      )}

      {showCreditDialog && (
        <UpsertCreditTypeDialog
          openCreateProductDialog={showCreditDialog}
          onClose={handleDialogClose}
          onProductCreated={handleProductCreated}
          onBackToMain={handleReturnToMain}
        />
      )}
    </>
  );
};

const ProductCreationStepperDialogWithSuspense = withSuspenseBoundary(ProductCreationStepperDialog);

export { ProductCreationStepperDialogWithSuspense as ProductCreationStepperDialog };
